import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/translations';
import { Button } from '@/components/ui/button';

const Header = () => {
  const { language, switchLanguage } = useLanguage();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: t('nav.home', language) },
    { path: '/matrimoni', label: t('nav.weddings', language) },
    { path: '/famiglia', label: t('nav.family', language) },
    { path: '/eventi', label: t('nav.events', language) },
    { path: '/ritratti', label: t('nav.portraits', language) },
    { path: '/chi-sono', label: t('nav.about', language) },
    { path: '/faq', label: t('nav.faq', language) },
    { path: '/contatti', label: t('nav.contact', language) }
  ];

  const isHomePage = location.pathname === '/';

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHomePage
          ? 'backdrop-blur-xl bg-background/90 border-b border-border/40 shadow-sm'
          : 'backdrop-blur-md bg-black/20'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            data-testid="logo-link"
            className="block"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_remember-truly/artifacts/36pm104c_Logo%20Grigio%202024.png"
              alt="Matteo Giacometti Photography"
              className={`h-12 w-auto transition-all duration-300 ${
                isScrolled || !isHomePage ? 'brightness-100' : 'brightness-0 invert'
              }`}
              data-testid="logo-image"
            />
          </Link>

          <nav className="hidden lg:flex items-center space-x-8" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-link-${item.path.replace('/', '') || 'home'}`}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-primary ${
                  location.pathname === item.path
                    ? 'text-primary'
                    : isScrolled || !isHomePage
                    ? 'text-foreground/80'
                    : 'text-white/90'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2" data-testid="language-switcher">
              <Button
                variant={language === 'it' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => switchLanguage('it')}
                data-testid="lang-button-it"
                className="h-8 px-3 text-xs"
              >
                IT
              </Button>
              <Button
                variant={language === 'en' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => switchLanguage('en')}
                data-testid="lang-button-en"
                className="h-8 px-3 text-xs"
              >
                EN
              </Button>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className={`lg:hidden flex flex-col space-y-1.5 p-2 ${
                isScrolled || !isHomePage ? 'text-foreground' : 'text-white'
              }`}
              aria-label="Toggle menu"
            >
              <span className={`block w-6 h-0.5 transition-all ${isScrolled || !isHomePage ? 'bg-foreground' : 'bg-white'}`}></span>
              <span className={`block w-6 h-0.5 transition-all ${isScrolled || !isHomePage ? 'bg-foreground' : 'bg-white'}`}></span>
              <span className={`block w-6 h-0.5 transition-all ${isScrolled || !isHomePage ? 'bg-foreground' : 'bg-white'}`}></span>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <nav
            data-testid="mobile-menu"
            className="lg:hidden mt-4 pb-4 border-t border-border/20 pt-4"
          >
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${item.path.replace('/', '') || 'home'}`}
                className={`block py-2 text-sm font-medium tracking-wide transition-colors hover:text-primary ${
                  location.pathname === item.path
                    ? 'text-primary'
                    : isScrolled || !isHomePage
                    ? 'text-foreground/80'
                    : 'text-white/90'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
